import { flatten } from "ramda";
import { type FC, type JSX } from "react";
import { Navigate, Route, Routes } from "react-router";
import { LoadingSpinner } from "widgets";

import styleLayout from "../styles/PageLayout.module.scss";
import { type INavigationContextProps } from "./ContextWidget.js";
import { type INavPanelCategory, NavigationPanel } from "./NavigationPanel.js";
import PageHeader from "./PageHeader.js";
import { type IRouteNavItemProps } from "./RouteNavItem.js";

interface INavigationPanelProps {
  navPanelCategories: INavPanelCategory[];
  navPanelCollapsed?: boolean;
  contextWidgetProps?: INavigationContextProps;
  header?: JSX.Element;
  isLoading?: boolean;
}

const PageLayout: FC<INavigationPanelProps> = (props) => {
  if (!props.navPanelCategories?.length) {
    return (
      <>
        <PageHeader isLoading />
        <div className={styleLayout.page}>
          <LoadingSpinner inCenter />
        </div>
      </>
    );
  }

  const navPanelCategories = props.navPanelCategories?.filter((c) => !!c);
  const defaultNavItem =
    flatten<IRouteNavItemProps>(navPanelCategories.map((navCategory) => navCategory.navItems)).find(
      (navItem) => navItem.default,
    ) || navPanelCategories[0].navItems[0];

  // @see RouteNavItem.tsx
  return (
    <>
      <NavigationPanel
        navPanelCategories={navPanelCategories}
        contextWidgetProps={props.contextWidgetProps}
        isCollapsed={props.navPanelCollapsed}
      />
      {props.header}
      {props.isLoading ? (
        <>
          <PageHeader isLoading />
          <div className={styleLayout.page}>
            <LoadingSpinner inCenter />
          </div>
        </>
      ) : (
        <Routes>
          {flatten<IRouteNavItemProps>(navPanelCategories.map((navCategory) => navCategory.navItems)).map(
            (navItem, i) => {
              return (
                <Route
                  path={buildRouteFromNavItem(navItem)}
                  key={navItem.routeKey || "page-layout-" + i}
                  element={navItem.pageComponent}
                ></Route>
              );
            },
          )}
          {defaultNavItem && (
            <Route
              path={"/*"}
              element={
                <Navigate
                  replace
                  to={{
                    pathname: defaultNavItem.route,
                    search: defaultNavItem.search ?? window.location.search,
                  }}
                />
              }
            />
          )}
        </Routes>
      )}
    </>
  );
};

const buildRouteFromNavItem = (navItem: IRouteNavItemProps) => {
  const matchingRoute = navItem.matchingRoute || navItem.route;
  const path = `${matchingRoute}${navItem.exact === true ? "" : "/*"}`;
  return path;
};

export default PageLayout;
